import { WAIT_UNITS, type WaitUnit } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Select } from "@sagot/ui";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import { IntegerInput } from "../../../workspace/shared";
import { capLabel, maxForUnit, waitCapMinutes, waitExceedsPlan } from "./wait-model";

const F = "engage.workflows.fields";

export interface Duration {
  value: number;
  unit: WaitUnit;
}

interface DurationFieldProps {
  legend: string;
  duration: Duration;
  disabled: boolean;
  onChange: (duration: Duration) => void;
}

/** A length of time (an amount and a unit), held to what the workspace's plan allows for a wait; says so when it is too long. */
export function DurationField({ legend, duration, disabled, onChange }: DurationFieldProps) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const cap = capLabel(waitCapMinutes(plan));
  const tooLong = waitExceedsPlan(duration, plan);
  return (
    <fieldset className="space-y-1.5">
      <legend className="label">{legend}</legend>
      <div className="flex gap-2">
        <IntegerInput aria-label={t(`${F}.waitValue`)} className="w-28" min={1} max={maxForUnit(duration.unit, plan)} disabled={disabled} value={duration.value} onChange={(value) => { onChange({ ...duration, value }); }} />
        <Select aria-label={t(`${F}.waitUnit`)} className="w-36" disabled={disabled} value={duration.unit} onChange={(e) => { onChange({ ...duration, unit: e.target.value as WaitUnit }); }}>
          {WAIT_UNITS.map((unit) => <option key={unit} value={unit}>{t(`${F}.waitUnits.${unit}`)}</option>)}
        </Select>
      </div>
      <p className={tooLong ? "text-xs font-semibold text-danger" : "hint"} role={tooLong ? "alert" : undefined}>
        {t(tooLong ? `${F}.waitTooLong` : `${F}.waitCap`, { value: cap.value, unit: t(`${F}.waitUnits.${cap.unit}`) })}
      </p>
    </fieldset>
  );
}
